// src/modules/operaciones/inventarios/components/InformeViewer.js
//
// Orquestador del dashboard de informes de inventario.
//
//   <InformeViewer datos={parseado} />
//   <InformeViewer datos={parseado} cargando={cargando} titulo="Mermas octubre" acciones={<MisBotones />} />
//
// Props
//   datos      parseado del backend ({ columnas, filas, meta, ... }). También acepta un informe guardado
//              antiguo que solo traiga { insights, graficoBarras, graficoTorta }.
//   cargando   true → muestra un esqueleto de carga.
//   titulo     título del encabezado (por defecto, el nombre del archivo).
//   acciones   nodo opcional que se muestra a la izquierda del botón "Columnas" (guardar, volver, etc.).
//   className  clases extra para el contenedor.
//
// Qué muestra
//   - Modo mermas:   encabezado + filtros + pestañas (Resumen, Bodegas, Fabricantes, Artículos, Alertas, Datos).
//   - Modo genérico: encabezado + filtros (si aplican) + resumen genérico (insights, barras y torta), sin pestañas.
//   - Cargando / sin datos / error: mensajes amigables.

import React, { useState } from "react";
import { AlertCircle, BarChart3, FileSpreadsheet, Inbox, Lightbulb, Loader2, RotateCcw } from "lucide-react";
import { Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { useDashboard } from "../hooks/useDashboard";
import { fmtNumero } from "../utils/informesComun";
import { FiltrosBar } from "../dashboard/FiltrosBar";
import { MapeoColumnas, BotonMapeoColumnas } from "../dashboard/MapeoColumnas";
import PanelResumen from "../dashboard/PanelResumen";
import PanelBodegas from "../dashboard/PanelBodegas";
import PanelFabricantes from "../dashboard/PanelFabricantes";
import PanelArticulos from "../dashboard/PanelArticulos";
import PanelAlertas from "../dashboard/PanelAlertas";
import TablaDetalle from "../dashboard/TablaDetalle";

/* ========================================================================== */
/* Constantes y utilidades                                                    */
/* ========================================================================== */

// Pestaña → componente. Todos reciben { analisis, dashboard }.
const PANELES = {
  resumen: PanelResumen,
  bodegas: PanelBodegas,
  fabricantes: PanelFabricantes,
  articulos: PanelArticulos,
  alertas: PanelAlertas,
  datos: TablaDetalle,
};

const COLORES = ["#2563eb", "#16a34a", "#f59e0b", "#dc2626", "#7c3aed", "#0891b2", "#db2777", "#65a30d"];

const compacto = new Intl.NumberFormat("es-CO", { notation: "compact", maximumFractionDigits: 1 });
const fmtEje = (v) => (Number.isFinite(Number(v)) ? compacto.format(Number(v)) : "");
const truncar = (s, n = 18) => {
  const t = String(s ?? "");
  return t.length > n ? `${t.slice(0, n - 1)}…` : t;
};

const idPestana = (id) => `informe-pestana-${id}`;
const idPanel = (id) => `informe-panel-${id}`;

/* ========================================================================== */
/* Mensajes de estado                                                         */
/* ========================================================================== */

function Mensaje({ icono: Icono = Inbox, titulo, texto, tono = "neutro", children }) {
  const colores =
    tono === "error"
      ? { caja: "border-red-200 bg-red-50", icono: "text-red-400", titulo: "text-red-800", texto: "text-red-700" }
      : { caja: "border-gray-300 bg-white", icono: "text-gray-300", titulo: "text-gray-800", texto: "text-gray-500" };
  return (
    <div
      role={tono === "error" ? "alert" : "status"}
      className={`flex flex-col items-center justify-center text-center border border-dashed rounded-xl py-14 px-4 ${colores.caja}`}
    >
      <Icono className={`w-10 h-10 mb-3 ${colores.icono}`} aria-hidden="true" />
      <p className={`text-sm font-semibold ${colores.titulo}`}>{titulo}</p>
      {texto && <p className={`text-sm mt-1 max-w-md ${colores.texto}`}>{texto}</p>}
      {children && <div className="mt-4">{children}</div>}
    </div>
  );
}

function EsqueletoCarga() {
  return (
    <div className="space-y-4" role="status" aria-live="polite" aria-busy="true">
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
        Cargando informe…
      </div>
      <div className="h-14 rounded-xl bg-gray-100 animate-pulse" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-24 rounded-xl bg-gray-100 animate-pulse" />
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="h-72 rounded-xl bg-gray-100 animate-pulse" />
        <div className="h-72 rounded-xl bg-gray-100 animate-pulse" />
      </div>
    </div>
  );
}

/* ========================================================================== */
/* Protección: si un panel falla, no se cae todo el dashboard                 */
/* ========================================================================== */

class PanelSeguro extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error(`Error al mostrar la pestaña "${this.props.nombre}":`, error, info);
  }

  render() {
    if (this.state.error) {
      return (
        <Mensaje
          icono={AlertCircle}
          tono="error"
          titulo="No se pudo mostrar esta pestaña"
          texto="Ocurrió un error al dibujar los gráficos. Prueba con otra pestaña o ajusta los filtros."
        />
      );
    }
    return this.props.children;
  }
}

/* ========================================================================== */
/* Resumen genérico (otros Excel y informes guardados antiguos)               */
/* ========================================================================== */

function TarjetaGrafico({ titulo, icono: Icono, children }) {
  return (
    <section className="bg-white border border-gray-200 rounded-xl shadow-sm p-4">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-800 mb-3">
        <Icono className="w-4 h-4 text-blue-600" aria-hidden="true" />
        {titulo}
      </h3>
      {children}
    </section>
  );
}

function ResumenGenerico({ resumen }) {
  const insights = Array.isArray(resumen?.insights) ? resumen.insights : [];
  const barras = Array.isArray(resumen?.graficoBarras) ? resumen.graficoBarras : [];
  const torta = Array.isArray(resumen?.graficoTorta) ? resumen.graficoTorta : [];

  if (insights.length === 0 && barras.length === 0 && torta.length === 0) {
    return (
      <Mensaje
        titulo="No hay nada para resumir"
        texto="El archivo no tiene columnas de texto o números que se puedan graficar. Revisa la asignación de columnas."
      />
    );
  }

  return (
    <div className="space-y-4">
      {insights.length > 0 && (
        <TarjetaGrafico titulo="Hallazgos" icono={Lightbulb}>
          <ul className="space-y-2">
            {insights.map((texto, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0" aria-hidden="true" />
                <span>{texto}</span>
              </li>
            ))}
          </ul>
        </TarjetaGrafico>
      )}

      {(barras.length > 0 || torta.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {barras.length > 0 && (
            <TarjetaGrafico titulo="Top 5 por valor" icono={BarChart3}>
              <div style={{ width: "100%", height: 300 }}>
                <ResponsiveContainer>
                  <BarChart data={barras} layout="vertical" margin={{ top: 4, right: 16, bottom: 4, left: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" tickFormatter={fmtEje} tick={{ fontSize: 11 }} />
                    <YAxis
                      type="category"
                      dataKey="name"
                      width={120}
                      tick={{ fontSize: 11 }}
                      tickFormatter={(v) => truncar(v, 18)}
                    />
                    <Tooltip formatter={(v) => fmtNumero(v)} />
                    <Bar dataKey="value" name="Valor" radius={[0, 4, 4, 0]}>
                      {barras.map((_, i) => (
                        <Cell key={i} fill={COLORES[i % COLORES.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </TarjetaGrafico>
          )}

          {torta.length > 0 && (
            <TarjetaGrafico titulo="Distribución de registros" icono={BarChart3}>
              <div style={{ width: "100%", height: 300 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={torta} dataKey="value" nameKey="name" innerRadius="50%" outerRadius="80%" paddingAngle={2}>
                      {torta.map((_, i) => (
                        <Cell key={i} fill={COLORES[i % COLORES.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => fmtNumero(v)} />
                    <Legend verticalAlign="bottom" formatter={(v) => truncar(v, 22)} wrapperStyle={{ fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </TarjetaGrafico>
          )}
        </div>
      )}
    </div>
  );
}

/* ========================================================================== */
/* Encabezado                                                                 */
/* ========================================================================== */

function Encabezado({ titulo, subtitulo, acciones, botonColumnas }) {
  return (
    <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-white border border-gray-200 rounded-xl shadow-sm px-4 py-3">
      <div className="flex items-center gap-3 min-w-0">
        <span className="flex-shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-lg bg-blue-50 text-blue-600">
          <FileSpreadsheet className="w-5 h-5" aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-gray-900 truncate" title={titulo}>
            {titulo}
          </h2>
          {subtitulo && <p className="text-xs text-gray-500 truncate">{subtitulo}</p>}
        </div>
      </div>
      {(acciones || botonColumnas) && (
        <div className="flex flex-wrap items-center gap-2 sm:justify-end">
          {acciones}
          {botonColumnas}
        </div>
      )}
    </header>
  );
}

/* ========================================================================== */
/* Componente principal                                                       */
/* ========================================================================== */

export function InformeViewer({ datos = null, cargando = false, titulo = "", acciones = null, className = "" }) {
  const d = useDashboard(datos);
  const [mapeoAbierto, setMapeoAbierto] = useState(false);

  const { analisis, esMermas, error, pestana, setPestana, pestanasDisponibles } = d;

  // Informe guardado antiguo: sin tabla original, pero con el resumen ya calculado
  const resumenGuardado =
    !analisis && !error && datos && (datos.insights || datos.graficoBarras || datos.graficoTorta) ? datos : null;

  const nombreArchivo =
    titulo ||
    analisis?.meta?.nombreArchivo ||
    datos?.meta?.nombreArchivo ||
    datos?.nombreArchivo ||
    "Informe de inventario";

  /* ---------- Cargando / sin datos ---------- */
  if (cargando) {
    return (
      <div className={`w-full ${className}`}>
        <EsqueletoCarga />
      </div>
    );
  }

  if (!datos) {
    return (
      <div className={`w-full ${className}`}>
        <Mensaje
          titulo="Aún no hay un informe para mostrar"
          texto="Sube un archivo de Excel o abre un informe guardado para ver el dashboard."
        />
      </div>
    );
  }

  /* ---------- Error de análisis ---------- */
  if (error) {
    return (
      <div className={`w-full space-y-4 ${className}`}>
        <Encabezado titulo={nombreArchivo} acciones={acciones} />
        <Mensaje
          icono={AlertCircle}
          tono="error"
          titulo="No se pudo analizar el archivo"
          texto={
            d.hayMapeo
              ? "Es posible que la asignación manual de columnas no sea válida. Restablécela para volver a la detección automática."
              : "Revisa que el archivo tenga encabezados y filas de datos. Si el problema continúa, vuelve a subirlo."
          }
        >
          {d.hayMapeo && (
            <button
              type="button"
              onClick={d.restablecerMapeo}
              className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-sm text-red-700 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-400"
            >
              <RotateCcw className="w-4 h-4" aria-hidden="true" />
              Restablecer columnas
            </button>
          )}
        </Mensaje>
      </div>
    );
  }

  /* ---------- Informe guardado antiguo (sin tabla) ---------- */
  if (resumenGuardado) {
    return (
      <div className={`w-full space-y-4 ${className}`}>
        <Encabezado titulo={nombreArchivo} subtitulo="Informe guardado · resumen" acciones={acciones} />
        <ResumenGenerico resumen={resumenGuardado} />
      </div>
    );
  }

  if (!analisis) {
    return (
      <div className={`w-full space-y-4 ${className}`}>
        <Encabezado titulo={nombreArchivo} acciones={acciones} />
        <Mensaje
          titulo="El informe no tiene datos legibles"
          texto="No se encontraron columnas ni filas para analizar. Verifica el archivo e inténtalo de nuevo."
        />
      </div>
    );
  }

  /* ---------- Dashboard ---------- */
  const subtitulo = [
    esMermas ? "Informe de mermas por bodega" : "Informe de inventario",
    `${fmtNumero(analisis.totalFilas || 0)} filas`,
    `${fmtNumero(analisis.totalColumnas || 0)} columnas`,
  ].join(" · ");

  const alertas = Array.isArray(analisis.alertas) ? analisis.alertas : [];
  const nErrores = alertas.filter((a) => a.nivel === "error").length;
  const nAvisos = alertas.filter((a) => a.nivel === "advertencia").length;
  const nAlertas = nErrores + nAvisos;

  const Panel = PANELES[pestana];

  return (
    <div className={`w-full space-y-4 ${className}`}>
      <Encabezado
        titulo={nombreArchivo}
        subtitulo={subtitulo}
        acciones={acciones}
        botonColumnas={<BotonMapeoColumnas hayMapeo={d.hayMapeo} onClick={() => setMapeoAbierto(true)} />}
      />

      <FiltrosBar dashboard={d} />

      {esMermas ? (
        <>
          {/* Pestañas */}
          <div className="border-b border-gray-200 overflow-x-auto">
            <div role="tablist" aria-label="Secciones del informe" className="flex gap-1 min-w-max">
              {pestanasDisponibles.map((p) => {
                const activa = p.id === pestana;
                return (
                  <button
                    key={p.id}
                    id={idPestana(p.id)}
                    type="button"
                    role="tab"
                    aria-selected={activa}
                    aria-controls={idPanel(p.id)}
                    onClick={() => setPestana(p.id)}
                    className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
                      activa
                        ? "border-blue-600 text-blue-700"
                        : "border-transparent text-gray-500 hover:text-gray-800 hover:border-gray-300"
                    }`}
                  >
                    {p.etiqueta}
                    {p.id === "alertas" && nAlertas > 0 && (
                      <span
                        className={`inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-full text-xs font-semibold text-white ${
                          nErrores > 0 ? "bg-red-500" : "bg-amber-500"
                        }`}
                        title={`${nErrores} error(es), ${nAvisos} advertencia(s)`}
                      >
                        {nAlertas}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Panel activo */}
          <div id={idPanel(pestana)} role="tabpanel" aria-labelledby={idPestana(pestana)} tabIndex={0} className="focus:outline-none">
            <PanelSeguro key={pestana} nombre={pestana}>
              {Panel ? <Panel analisis={analisis} dashboard={d} /> : null}
            </PanelSeguro>
          </div>
        </>
      ) : (
        <ResumenGenerico resumen={analisis} />
      )}

      <MapeoColumnas dashboard={d} abierto={mapeoAbierto} onCerrar={() => setMapeoAbierto(false)} />
    </div>
  );
}

export default InformeViewer;